import type { Messages } from '../de';

export default {
  kicker: 'More',
  title: 'Gear',
  loading: 'Loading …',
  loadError: 'Gear not loaded: {error}',
  active: 'Active',
  retired: 'Retired',
  empty: 'No shoes yet. Add one – or import your Strava export.',
  add: 'New shoe',
  edit: 'Edit',
  save: 'Save',
  cancel: 'Cancel',
  delete: 'Delete',
  deleteConfirm: {
    one: 'Deleting removes the shoe from {count} activity. It then counts without shoes and won’t be asked about again. This can’t be undone – to keep the km, retire it instead.',
    other: 'Deleting removes the shoe from {count} activities. They then count without shoes and won’t be asked about again. This can’t be undone – to keep the km, retire it instead.',
  },
  deleteYes: 'Delete for good',
  retire: 'Retire',
  unretire: 'Bring back',
  retireHint: 'Retire: the shoe stays in the archive with its km, but is no longer offered and no longer warns.',
  unretireHint: 'Bring back: the shoe is offered again and warns again.',
  name: 'Name',
  namePlaceholder: 'e.g. HOKA Clifton 9',
  startKm: 'km before',
  startKmHelp: 'Already run before dashy-dash knew the shoe',
  warnKm: '“Replace soon” from km',
  limitKm: '“Replace” from km',
  limitHelp: 'Running shoes usually last 500–800 km. Empty = no limit.',
  noLimit: 'empty = none',
  counts: 'Counted: running, walking and hiking in the shoe, plus the km before.',
  uses: { one: '{count} activity', other: '{count} activities' },
  range: 'run {from} – {to}',
  ofStart: '{km} of it before',
  retiredOn: 'retired {date}',
  unused: 'not used yet',
  status: { warn: 'Replace soon', limit: 'Replace' },
  toWarn: '{km} left until “Replace soon” ({at})',
  toLimit: '{km} left until “Replace” ({at})',
  overLimit: '{km} over {at}',
  retiredShow: { one: 'Show retired ({count})', other: 'Show retired ({count})' },
  profile: {
    typical: 'typically {km} at {pace} (median of {count} runs)',
    hr: 'avg HR {hr}',
    rhythm: 'most often on {day} ({share}), {daytime}',
    days: { 0: 'Mondays', 1: 'Tuesdays', 2: 'Wednesdays', 3: 'Thursdays', 4: 'Fridays', 5: 'Saturdays', 6: 'Sundays' },
    daytime: { morning: 'in the morning', midday: 'around noon', afternoon: 'in the afternoon', evening: 'in the evening', night: 'at night' },
    fastestKm: 'fastest km {time}',
    longest: 'longest run {km}',
    temp: 'avg {temp}',
    wet: { one: 'rain on {count} of {of} runs', other: 'rain on {count} of {of} runs' },
    tooNew: 'Profile from {count} runs',
    forecast: 'at {perWeek} a week (last 90 days) around {month}',
    pause: '(last run on {date})',
    resting: 'last run on {date}, so no forecast',
    restingWorn: 'last run on {date} – retire it? That’s under “Edit”.',
    slow: 'only {km} in the last 90 days – too little for a forecast',
    story: 'Recap',
    storyAria: 'Recap: {name}',
  },
  charts: {
    kmTitle: 'km per shoe',
    kmSub: 'Active shoes from their first run, including the km before',
    timeSub: 'From the first to the last run in the shoe',
    view: 'View',
    viewKm: 'km',
    viewTime: 'When in use',
    xKm: 'Years in use',
    active: 'active',
    retired: 'retired',
    forecast: 'Forecast from the last 90 days',
    wornBand: 'where you retired shoes so far',
    idle: 'active, but not run since',
    lastRun: 'last {date}',
    tipKm: '{km} after {months} months',
    tipForecast: '“Replace soon” around {month}',
    tipForecastLimit: '“Replace” around {month}',
    kmTakeaway: { one: 'The {count} shoe that went past {min} km was retired at {from}.', other: 'The {count} shoes that went past {min} km were retired between {from} and {to}.' },
    timeTakeaway: 'Longest in service: {name}, {months} months.',
    chartLabel: 'km per active shoe over the years in use, with forecast',
    paceTitle: 'Pace per shoe',
    paceSub: 'Shoes with 10+ runs. The bar shows how fast half of your runs were, the tick the typical pace. Left is faster.',
    paceTakeaway: 'In the {fast} you typically run fastest ({fastPace}), in the {slow} easiest ({slowPace}).',
    paceRow: '{name}: {count} runs, typically {pace}, middle half between {from} and {to}',
    paceMeta: { one: '{pace} · {count} run', other: '{pace} · {count} runs' },
  },
  story: {
    kicker: 'Shoe recap',
    km: 'kilometres',
    runs: { one: '{count} run', other: '{count} runs' },
    walks: { one: ' and {count} walk or hike', other: ' and {count} walks or hikes' },
    span: '{runs}{walks}, {from} to {to}.',
    spanActive: '{runs}{walks} since {from} – and still going.',
    elevation: 'metres climbed',
    hours: 'hours out',
    months: 'months',
    rhythmKicker: 'Rhythm',
    rhythmTitle: 'Favourite: {day}, {daytime}',
    rhythmText: '{share} of the runs were on a {dayLong}. Favourite hour: {hour}:00.',
    rhythmCaption: 'Circle: runs by start time over 24 hours. Bars: runs per weekday.',
    bestKicker: 'Bests',
    bestTitle: 'What it could do',
    bestTitleActive: 'What it can do',
    fastestKm: 'fastest kilometre',
    longest: 'longest run',
    typical: 'typical run',
    avgHr: 'avg HR',
    wet: 'runs in the rain (of {of})',
    coldest: 'coldest run',
    thanks: 'Thanks for {km}.',
    still: 'Keep going – {km} to “Replace soon”.',
  },
  how: {
    profile: {
      label: 'Profile: how is this calculated?',
      km: 'km: running, walking and hiking in the shoe, plus the km it already had before.',
      typical: 'Typical: median of the runs in the shoe, distance and pace separately. Walks and hikes don’t count here.',
      pace: 'Pace from moving time (elapsed time if missing); below 2:30 or above 15:00 /km counts as a recording error.',
      daytime: 'Time of day by start time: morning 5–10, noon 11–13, afternoon 14–16, evening 17–21, night 22–4.',
      rain: 'Rain: 0.2 mm or more during the run (Open-Meteo at the start). Only runs with weather data count.',
      fastest: 'Fastest km: fastest 1 km stretch within a run, pauses included.',
      forecast: 'Forecast: the km of the last 90 days, or since the first run for younger shoes (walks and hikes included) carried forward evenly to the next limit. Only from 3 sessions and 14 days; none more than 2 years ahead.',
    },
    km: {
      lines: 'For each active shoe, the km after every day in it, starting with the km before. Running, walking and hiking count.',
      forecast: 'Dashed: the forecast from the profile up to the next limit, with the month at the point.',
      band: 'Grey band: from the lowest to the highest km at which you retired a shoe – only shoes from 300\u00a0km, and only once there are two; shorter ones count as sorted out, not worn out.',
      time: '“When in use”: bar from the first to the last run; for active shoes the thin line shows the time since.',
    },
    pace: {
      which: 'Runs only, shoes with 10+ runs. Pace per run from moving time (elapsed time if missing) and distance; below 2:30 or above 15:00 /km is dropped as a recording error.',
      bar: 'Bar: the middle half of the runs – a quarter were faster, a quarter slower.',
      median: 'Tick: median – half of the runs were faster, half slower.',
    },
  },
  ask: {
    title: 'Ask after a run',
    sub: 'When a new run has no shoes, “Today” asks – by push too, if you like.',
  },
  push: {
    title: 'Push on this device',
    sub: 'After a new run without shoes you get a message, nothing else.',
    test: 'Test message',
    testSent: 'Sent.',
    devices: { one: '{count} device registered', other: '{count} devices registered' },
    unsupported: 'This browser can’t receive push messages.',
    install: 'On iPhone, push only works once dashy-dash is installed (see above).',
    denied: 'Notifications are blocked in the browser – allow them in the site settings.',
    hint: 'Android shows the two most recently used shoes as buttons in the message; on iPhone a tap opens the card.',
  },
  install: {
    title: 'Install as an app',
    button: 'Install',
    prompt: 'Then it starts like an app, without the address bar.',
    done: 'Installed – open it from the new icon from now on.',
    ios1: 'In Safari, tap “Share” in the bar at the bottom:',
    ios2: 'Choose “Add to Home Screen” in the list (you may need to swipe down a bit).',
    ios3: 'Tap “Add” at the top right.',
    ios4: 'Open dashy-dash from the new icon. Then turn on push for the shoe question under “Gear”.',
    cardTitle: 'dashy-dash as an app',
    why: 'On the home screen dashy-dash starts like an app, without the address bar – and can ask about your shoes by push after a run.',
    dismiss: 'Don’t show again',
    shareIcon: 'Share icon: square with an arrow pointing up',
    android: 'In Chrome, tap ⋮ at the top right, then “Install app” or “Add to Home screen”. Push also works without it, right in the browser.',
    desktop: 'In Chrome or Edge, click the install icon on the right of the address bar; in Safari on a Mac, File → “Add to Dock”.',
  },
  prompt: {
    title: 'Which shoes?',
    skip: 'No shoes',
    later: 'Ask later',
    more: { one: 'Then the card asks about {count} more run.', other: 'Then the card asks about {count} more runs.' },
    otherAnswers: 'Or:',
    saved: '{name} saved',
  },
  activity: {
    label: 'Shoes',
    none: 'None',
    manage: 'Gear',
  },
} satisfies Messages['gear'];
